import { RevenueHistory } from '../services/revenuehistory/revenueHistoryService';
import { getTotalRevenue } from '../components/revenuehistory/utils';
import { startOfPeriod, addPeriod, normalizeDate } from './dateUtils';
import { TimeViewOption } from '../components/ui/TimeControls';
import { getWeek, getYear } from 'date-fns';
import { RevenueSourceType } from '../types/RevenueSourceType';
import { getItemAddOnsRevenue, getItemTotalRevenue, AddOnAggregation } from './revenueAddOns';


export interface TopRevenueItem {
  product: string;             // the real item name — every name-based lookup keys off this
  // Partner venue this row's revenue came from (e.g. "Bar POP"), and the label to SHOW.
  // A partner sells the restaurant's own dishes under the same names, so the raw name alone
  // cannot tell two rows apart; `displayName` is what the UI renders.
  venueLabel?: string;
  displayName: string;
  // The revenue source the row's items came from. Needed to edit the right source's
  // quantities when the same item name exists under both the restaurant and a partner.
  restaurantRevenueSourceId?: string;
  items: number;
  price: number;
  totalPrice: number;          // base + add-ons (used everywhere as the item's revenue)
  baseRevenue: number;         // base-only (needed for the Add-ons column / popover)
  addOnsRevenue: number;       // choices-only contribution to totalPrice
  // Choices for this aggregated item, merged by exact name across every revenue
  // record. Empty for items with no choices. Feeds the per-row Add-ons popover.
  addOnsBreakdown: AddOnAggregation[];
  menuCategory?: string;       // Cost price category name
}

/**
 * Partner-venue revenue categories. A venue that sells a restaurant's products on its behalf
 * stores its share under "<Venue> - <group>" category names (server side: the `storeAs` values
 * in `domains/revenueData/sources/scanfie/partners/barPopConfig.ts`). The venue prefix is
 * surfaced as a display suffix so a partner row is never mistaken for the restaurant's own
 * identically-named item — the stored item name itself stays untouched, because cost price,
 * waste and recipes all link to revenue items by that exact name.
 */
const PARTNER_VENUE_CATEGORY_PREFIXES = ['Bar POP'];

/** "Bar POP - Cocktails" → "Bar POP". Undefined for a restaurant's own categories. */
export function getRevenueVenueLabel(categoryName: string): string | undefined {
  const name = (categoryName ?? '').toLowerCase();
  return PARTNER_VENUE_CATEGORY_PREFIXES.find(prefix => name.startsWith(`${prefix.toLowerCase()} - `));
}

/** Item name as the UI shows it: suffixed with the partner venue when the row is a partner's. */
export function getRevenueItemDisplayName(itemName: string, venueLabel?: string): string {
  return venueLabel ? `${itemName} — ${venueLabel}` : itemName;
}

/**
 * Calculate total revenue from revenue history data
 */
export function calculateTotalRevenue(
  revenueData: RevenueHistory[],
  showExVat: boolean
): number {
  return revenueData.reduce((sum, r) => sum + getTotalRevenue(r, showExVat, true), 0);
}

export interface GraphDataPoint {
  label: string;
  value: number;
  periodStart: Date;
}

/**
 * Calculate graph data points for revenue over time.
 * Returns up to 12 data points (never going into the future) with 0 for missing data.
 *
 * @param revenueData - All revenue history data
 * @param selectedPeriodStart - The start date of the currently selected period
 * @param view - The view type (day/week/month/all)
 * @param showExVat - Whether to show excluding VAT
 * @returns Array of graph data points with labels and values
 */
export function calculateRevenueGraphData(
  revenueData: RevenueHistory[],
  selectedPeriodStart: Date,
  selectedPeriodEnd: Date,
  view: 'day' | 'week' | 'month' | 'quarter' | 'all',
  showExVat: boolean
): GraphDataPoint[] {
  // For 'all' view, use daily data points for the exact custom date range
  if (view === 'all') {
    return calculateCustomRangeGraphData(revenueData, selectedPeriodStart, selectedPeriodEnd, showExVat);
  }

  // Group revenue by period
  const periodMap = new Map<string, number>();

  revenueData.forEach(rev => {
    const revDate = normalizeDate(new Date(rev.date));
    const periodStart = startOfPeriod(revDate, view);
    const periodKey = periodStart.toISOString();
    periodMap.set(periodKey, (periodMap.get(periodKey) || 0) + getTotalRevenue(rev, showExVat));
  });

  // Get today and normalize it
  const today = normalizeDate(new Date());
  const todayPeriodStart = startOfPeriod(today, view);

  // Calculate 12 periods ending at the selected period (or today if selected is in the future)
  const maxPoints = 12;
  const selectedPeriodNormalized = startOfPeriod(selectedPeriodStart, view);

  // End at the selected period, but don't go beyond today
  const endPeriod = selectedPeriodNormalized > todayPeriodStart ? todayPeriodStart : selectedPeriodNormalized;

  // Start 11 periods before the end period
  let currentPeriod = new Date(endPeriod);
  for (let i = 0; i < maxPoints - 1; i++) {
    currentPeriod = addPeriod(currentPeriod, view, -1);
  }

  // Generate all 12 periods with data or 0
  const periods: GraphDataPoint[] = [];
  for (let i = 0; i < maxPoints; i++) {
    const periodKey = currentPeriod.toISOString();
    periods.push({
      label: formatPeriodLabel(currentPeriod, view),
      value: periodMap.get(periodKey) || 0,
      periodStart: new Date(currentPeriod)
    });
    currentPeriod = addPeriod(currentPeriod, view, 1);
  }

  return periods;
}

/**
 * Calculate daily graph data for custom date ranges
 */
function calculateCustomRangeGraphData(
  revenueData: RevenueHistory[],
  startDate: Date,
  endDate: Date,
  showExVat: boolean
): GraphDataPoint[] {
  const dailyMap = new Map<string, number>();

  // Group revenue by day
  revenueData.forEach(rev => {
    const revDate = normalizeDate(new Date(rev.date));

    // Only include data within the selected range
    if (revDate >= normalizeDate(startDate) && revDate <= normalizeDate(endDate)) {
      const dayKey = revDate.toISOString();
      dailyMap.set(dayKey, (dailyMap.get(dayKey) || 0) + getTotalRevenue(rev, showExVat));
    }
  });

  // Don't go into the future
  const today = normalizeDate(new Date());
  const effectiveEndDate = endDate > today ? today : endDate;

  // Create array of all days in range (including days with no data)
  const allDays: GraphDataPoint[] = [];
  const currentDay = new Date(startDate);
  currentDay.setHours(0, 0, 0, 0);

  while (currentDay <= effectiveEndDate) {
    const dayKey = currentDay.toISOString();
    allDays.push({
      label: formatPeriodLabel(new Date(currentDay), 'day'),
      value: dailyMap.get(dayKey) || 0,
      periodStart: new Date(currentDay)
    });
    currentDay.setDate(currentDay.getDate() + 1);
  }

  return allDays;
}

/**
 * Format period label for graph display
 */
function formatPeriodLabel(date: Date, view: 'day' | 'week' | 'month' | 'quarter'): string {
  if (view === 'day') {
    // Short format: "Jan 15"
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } else if (view === 'week') {
    // Week format: "W2" (week number)
    const yearStart = new Date(date.getFullYear(), 0, 1);
    const weekStart = startOfPeriod(date, 'week');
    const diff = weekStart.getTime() - yearStart.getTime();
    const weekNumber = Math.floor(diff / (1000 * 60 * 60 * 24 * 7)) + 1;
    return `W${weekNumber}`;
  } else if (view === 'month') {
    // Month format: "Jan", "Feb", etc.
    return date.toLocaleDateString('en-US', { month: 'short' });
  } else if (view === 'quarter') {
    // Quarter format: "Q1 YY", e.g., "Q3 25"
    const quarter = Math.floor(date.getMonth() / 3) + 1;
    const yearShort = date.getFullYear() % 100;
    return `Q${quarter} ${yearShort}`;
  }
  return '';
}

/**
 * Calculate top revenue items from filtered revenue data
 * @param revenueData - Revenue history data
 * @param showExVat - Whether to show excluding VAT
 * @param costPriceAnalyses - Optional cost price analyses for menu category lookup
 * @param costPriceCategories - Optional cost price categories for menu category names
 * @returns Array of top revenue items formatted for TableComponent
 */
export function calculateTopRevenueItems(
  revenueData: RevenueHistory[],
  showExVat: boolean,
  costPriceAnalyses?: any[],
  costPriceCategories?: any[]
): TopRevenueItem[] {
  // Tracks both halves so the items view can show an Add-ons column
  // (`addOnsRevenue`) alongside the full per-item total (`baseRevenue + addOnsRevenue`).
  // `choices` is the merge-by-name breakdown for the per-row popover.
  // `name` is carried in the value, never re-derived from the key: a category name
  // containing a hyphen (e.g. "Bar POP - Untable") would otherwise split in the wrong
  // place and glue part of the category onto the item name.
  const itemMap = new Map<string, {
    name: string;
    venueLabel?: string;
    restaurantRevenueSourceId?: string;
    quantity: number;
    baseRevenue: number;
    addOnsRevenue: number;
    choices: Map<string, AddOnAggregation>;
  }>();

  // Aggregate revenue items
  revenueData.forEach(r => {
    r.revenueData.categories.forEach(category => {
      category.items.forEach(item => {
        // No price gate: €0 items (POS lines whose revenue is booked elsewhere)
        // and negative items (discount lines) are shown too, so item counts match
        // the cost-price and setup-score universes.
        const key = `${item.name}-${category.name}`;

        if (!itemMap.has(key)) {
          itemMap.set(key, {
            name: item.name,
            venueLabel: getRevenueVenueLabel(category.name),
            restaurantRevenueSourceId: r.restaurantRevenueSourceId,
            quantity: 0,
            baseRevenue: 0,
            addOnsRevenue: 0,
            choices: new Map(),
          });
        }

        const agg = itemMap.get(key)!;
        agg.quantity += (item.quantity || 0);
        agg.baseRevenue += showExVat ? (item.exVat || 0) : (item.price || 0);
        agg.addOnsRevenue += getItemAddOnsRevenue(item, showExVat);

        // Merge this item's choices into the per-item breakdown so the Add-ons
        // popover can show the full picture across all revenue records.
        for (const choice of item.choices ?? []) {
          const choiceRevenue = showExVat ? (choice.exVat || 0) : (choice.price || 0);
          const existing = agg.choices.get(choice.name);
          if (existing) {
            existing.quantity += choice.quantity || 0;
            existing.revenue += choiceRevenue;
          } else {
            agg.choices.set(choice.name, {
              name: choice.name,
              quantity: choice.quantity || 0,
              revenue: choiceRevenue,
            });
          }
        }
      });
    });
  });

  // Helper to get menu category name for an item
  const getMenuCategoryName = (itemName: string): string | undefined => {
    if (!costPriceAnalyses || !costPriceCategories) return undefined;

    const analysis = costPriceAnalyses.find(a => a.menuItemName === itemName);
    if (!analysis || !analysis.categoryId) return 'Unassigned';

    const category = costPriceCategories.find(c => c.id === analysis.categoryId);
    return category ? category.name : 'Unknown Category';
  };

  // Transform to TopRevenueItem format and sort by revenue (total = base + add-ons)
  return Array.from(itemMap.entries())
    .map(([, { name, venueLabel, restaurantRevenueSourceId, quantity, baseRevenue, addOnsRevenue, choices }]) => {
      const totalPrice = baseRevenue + addOnsRevenue;
      const addOnsBreakdown = Array.from(choices.values()).sort((a, b) => b.revenue - a.revenue);
      return {
        product: name,
        venueLabel,
        displayName: getRevenueItemDisplayName(name, venueLabel),
        restaurantRevenueSourceId,
        items: quantity,
        price: quantity > 0 ? totalPrice / quantity : 0,
        totalPrice,
        baseRevenue,
        addOnsRevenue,
        addOnsBreakdown,
        menuCategory: getMenuCategoryName(name)
      };
    })
    .sort((a, b) => b.totalPrice - a.totalPrice)
}

export function calculateItemsSold(revenueData: RevenueHistory[]): number {
  // Items sold = base `item.quantity` only. Choices are not separate sold units
  // (they ride on top of a base item), so we never iterate `item.choices` here.
  var itemsSold = 0
  revenueData.forEach(r => {
    r.revenueData.categories.forEach(category => {
      category.items.forEach(item => {
        // >= 0: €0 lines are real sold items; negative lines are discounts, not units.
        if (item.price >= 0) {
          itemsSold += (item.quantity || 0);
        }
      });
    });
  });

  return itemsSold
}

/**
 * Helper function to calculate total revenue for a single revenue history entry.
 * Sums per-item (base + nested choices) — `category.price` would also work, but
 * this branch is shared with the item-level breakdown above and keeps them aligned.
 */
function calculateEntryRevenue(entry: RevenueHistory, showExVatOrders: boolean): number {
  let revenue = 0;
  for (const category of entry.revenueData.categories) {
    for (const item of category.items) {
      revenue += getItemTotalRevenue(item, showExVatOrders);
    }
  }
  return revenue;
}

export function calculateAverageRevenue(revenueData: RevenueHistory[], showExVatOrders: boolean, selectedTimeOption: TimeViewOption) : number {
  var averageRevenue = 0
  switch (selectedTimeOption) {
    case 'day':
      averageRevenue = getDailyRevenueAverage(revenueData, showExVatOrders)
      break;
    case 'week':
      averageRevenue = getWeeklyRevenueAverage(revenueData, showExVatOrders)
      break;
    case 'month':
      averageRevenue = calculateAverageMonthlyRevenue(revenueData, showExVatOrders)
      break;
    case 'all':
    default:
      averageRevenue = 0
  }

  return averageRevenue
}

export function getDailyRevenueAverage(
  revenueData: RevenueHistory[],
  showExVatOrders: boolean
): number {
  if (revenueData.length === 0) return 0;

  const todayKey = new Date().toISOString().split('T')[0];

  // Group revenue by unique date, excluding today (incomplete)
  const dailyRevenueMap = new Map<string, number>();
  revenueData.forEach(entry => {
    const dateKey = new Date(entry.date).toISOString().split('T')[0];
    if (dateKey === todayKey) return;
    dailyRevenueMap.set(dateKey, (dailyRevenueMap.get(dateKey) || 0) + calculateEntryRevenue(entry, showExVatOrders));
  });

  // Only include days with non-zero revenue
  const nonZeroDays = Array.from(dailyRevenueMap.values()).filter(rev => rev > 0);
  if (nonZeroDays.length === 0) return 0;

  const totalRevenue = nonZeroDays.reduce((sum, rev) => sum + rev, 0);
  return totalRevenue / nonZeroDays.length;
}

export function getWeeklyRevenueAverage(
  revenueData: RevenueHistory[],
  showExVatOrders: boolean
): number {
  if (revenueData.length === 0) return 0;

  const today = normalizeDate(new Date());
  const currentWeekKey = getYear(today) * 100 + getWeek(today);

  // Group all data by week, excluding current (incomplete) week
  const weekMap = new Map<number, number>();
  revenueData.forEach(entry => {
    const entryDate = normalizeDate(new Date(entry.date));
    const weekKey = getYear(entryDate) * 100 + getWeek(entryDate);
    if (weekKey === currentWeekKey) return;

    weekMap.set(weekKey, (weekMap.get(weekKey) || 0) + calculateEntryRevenue(entry, showExVatOrders));
  });

  // Only include weeks with non-zero revenue
  const nonZeroWeeks = Array.from(weekMap.values()).filter(rev => rev > 0);
  if (nonZeroWeeks.length === 0) return 0;

  const totalRevenue = nonZeroWeeks.reduce((sum, rev) => sum + rev, 0);
  return totalRevenue / nonZeroWeeks.length;
}


export function calculateAverageMonthlyRevenue(
  revenueData: RevenueHistory[],
  showExVatOrders: boolean
): number {
  if (revenueData.length === 0) return 0;

  const today = new Date();
  const currentMonthKey = `${today.getFullYear()}-${today.getMonth()}`;

  // Group all data by month, excluding current (incomplete) month
  const monthMap = new Map<string, number>();
  revenueData.forEach(entry => {
    const entryDate = normalizeDate(new Date(entry.date));
    const monthKey = `${getYear(entryDate)}-${entryDate.getMonth()}`;
    if (monthKey === currentMonthKey) return;

    monthMap.set(monthKey, (monthMap.get(monthKey) || 0) + calculateEntryRevenue(entry, showExVatOrders));
  });

  // Only include months with non-zero revenue
  const nonZeroMonths = Array.from(monthMap.values()).filter(rev => rev > 0);
  if (nonZeroMonths.length === 0) return 0;

  const totalRevenue = nonZeroMonths.reduce((sum, rev) => sum + rev, 0);
  return totalRevenue / nonZeroMonths.length;
}

/**
 * Calculate revenue mix (Food vs Drinks split) from revenue data
 * @param revenueData - Revenue history data
 * @param showExVat - Whether to show excluding VAT
 * @returns Object with food and drinks values and percentages
 */
export interface RevenueMix {
  food: {
    value: number;
    percentage: number;
  };
  drinks: {
    value: number;
    percentage: number;
  };
}

export function calculateRevenueMix(
  revenueData: RevenueHistory[],
  showExVat: boolean
): RevenueMix {
  let foodRevenue = 0;
  let drinksRevenue = 0;

  revenueData.forEach(revenue => {
    const revenueValue = getTotalRevenue(revenue, showExVat, true);
    
    if (revenue.revenueSourceType === RevenueSourceType.FOOD) {
      foodRevenue += revenueValue;
    } else if (revenue.revenueSourceType === RevenueSourceType.DRINKS) {
      drinksRevenue += revenueValue;
    }
  });

  const totalRevenue = foodRevenue + drinksRevenue;
  
  return {
    food: {
      value: foodRevenue,
      percentage: totalRevenue > 0 ? (foodRevenue / totalRevenue) * 100 : 0,
    },
    drinks: {
      value: drinksRevenue,
      percentage: totalRevenue > 0 ? (drinksRevenue / totalRevenue) * 100 : 0,
    },
  };
}

/**
 * Calculate percentage of total revenue for an item
 * @param itemRevenue - Revenue for a specific item
 * @param totalRevenue - Total revenue to compare against
 * @returns Percentage value (0-100)
 */
export function calculatePercentageOfTotal(
  itemRevenue: number,
  totalRevenue: number
): number {
  if (totalRevenue === 0 || itemRevenue === 0) return 0;
  return (itemRevenue / totalRevenue) * 100;
}

/**
 * Aggregate revenue items by menu category
 * @param revenueData - Revenue history data
 * @param showExVat - Whether to show excluding VAT
 * @param costPriceAnalyses - Cost price analyses for menu category lookup
 * @param categories - Cost price categories for category names
 * @returns Array of category revenue items with aggregated data
 */
export interface CategoryRevenueItem {
  categoryName: string;
  itemSold: number;
  totalRevenue: number;
  avgItemPrice: number;
  percentageOfTotal: number;
  subItems?: TopRevenueItem[];
}

export function aggregateRevenueItemsByCategory(
  revenueData: RevenueHistory[],
  showExVat: boolean,
  costPriceAnalyses: any[],
  categories: any[],
  totalRevenue: number
): CategoryRevenueItem[] {
  const categoryMap = new Map<string, {
    items: TopRevenueItem[];
    totalQuantity: number;
    totalRevenue: number;
  }>();

  // First, get all individual items grouped by category
  const allItems = calculateTopRevenueItems(revenueData, showExVat, costPriceAnalyses, categories);

  allItems.forEach(item => {
    const categoryName = item.menuCategory || 'Unassigned';
    
    if (!categoryMap.has(categoryName)) {
      categoryMap.set(categoryName, {
        items: [],
        totalQuantity: 0,
        totalRevenue: 0,
      });
    }

    const categoryData = categoryMap.get(categoryName)!;
    categoryData.items.push(item);
    categoryData.totalQuantity += item.items;
    categoryData.totalRevenue += item.totalPrice;
  });

  // Transform to CategoryRevenueItem format
  return Array.from(categoryMap.entries())
    .map(([categoryName, data]) => {
      const avgItemPrice = data.totalQuantity > 0 
        ? data.totalRevenue / data.totalQuantity 
        : 0;
      
      return {
        categoryName,
        itemSold: data.totalQuantity,
        totalRevenue: data.totalRevenue,
        avgItemPrice,
        percentageOfTotal: calculatePercentageOfTotal(data.totalRevenue, totalRevenue),
        subItems: data.items.sort((a, b) => b.totalPrice - a.totalPrice),
      };
    })
    .sort((a, b) => b.totalRevenue - a.totalRevenue);
}